// Production highlight + auto-dwell controller for the dock firefly (V10).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import type { DockEdge } from "../activityDockPositionMath";
import {
  fireflyAnchorInTab,
  fireflyHighlightInTab,
} from "./fireflyDockGeometry";
import {
  applyFireflySpeedMultiplier,
  getFireflyIntentParams,
} from "./fireflyIntents";
import { effectiveFireflyDwellMs } from "./fireflyMotion";
import { resolveFireflyIntent } from "./resolveFireflyIntent";
import { useFireflyAutoDwell } from "./useFireflyAutoDwell";
import { useFireflyMotionSpeedMultiplier } from "./useFireflyMotionSpeedMultiplier";

export function useActivityDockFireflyHighlight(options: {
  tone: KpiTone | null;
  edge: DockEdge;
  tabWidthPx: number;
  tabHeightPx: number;
  /** Pause auto-dwell + tone highlights while the panel is open. */
  pauseMotion: boolean;
}) {
  const { tone, edge, tabWidthPx, tabHeightPx, pauseMotion } = options;

  const intent = useMemo(() => resolveFireflyIntent(tone), [tone]);
  const [motionSpeedMultiplier] = useFireflyMotionSpeedMultiplier();
  const params = useMemo(() => {
    const base = getFireflyIntentParams(intent);
    if (!base) return null;
    return applyFireflySpeedMultiplier(base, motionSpeedMultiplier);
  }, [intent, motionSpeedMultiplier]);

  const dwellMs = useMemo(() => {
    if (!params) return 0;
    return effectiveFireflyDwellMs(
      params.dwellDurationMs,
      params,
      motionSpeedMultiplier,
      false,
    );
  }, [params, motionSpeedMultiplier]);
  const anchor = useMemo(
    () => fireflyAnchorInTab(edge, tabWidthPx, tabHeightPx),
    [edge, tabWidthPx, tabHeightPx],
  );

  const [highlightGen, setHighlightGen] = useState(0);
  const [highlightPoint, setHighlightPoint] = useState(anchor);
  const [anchorDwellReady, setAnchorDwellReady] = useState(true);
  const [highlightMotionSuppressed, setHighlightMotionSuppressed] = useState(false);

  const triggerHighlight = useCallback(() => {
    setHighlightPoint(fireflyHighlightInTab(edge, tabWidthPx, tabHeightPx));
    setHighlightGen((g) => g + 1);
  }, [edge, tabWidthPx, tabHeightPx]);

  const suppressHighlightMotion = useCallback(() => {
    setHighlightMotionSuppressed(true);
  }, []);

  const motionPaused = pauseMotion || highlightMotionSuppressed;

  const prevToneRef = useRef(tone);
  useEffect(() => {
    const prev = prevToneRef.current;
    prevToneRef.current = tone;
    if (motionPaused) return;
    if (tone === "red" && prev !== "red") {
      triggerHighlight();
      return;
    }
    if (tone === "amber" && prev !== "red" && prev !== "amber") {
      triggerHighlight();
    }
  }, [tone, motionPaused, triggerHighlight]);

  useFireflyAutoDwell({
    enabled: !motionPaused && Boolean(params?.dwellDurationMs),
    dwellMs,
    anchorDwellReady,
    paused: false,
    onDwellElapsed: triggerHighlight,
  });

  const onAnchorDwellReadyChange = useCallback((ready: boolean) => {
    setAnchorDwellReady(ready);
    if (ready) {
      setHighlightMotionSuppressed(false);
    }
  }, []);

  return {
    intent,
    anchor,
    highlightGen: highlightMotionSuppressed ? 0 : highlightGen,
    highlightPoint,
    anchorDwellReady,
    onAnchorDwellReadyChange,
    triggerHighlight,
    suppressHighlightMotion,
  };
}
